import { Draggable, Droppable } from '@hello-pangea/dnd'
import type { CSSProperties } from 'react'
import { cn } from '@/lib/utils'
import { TaskStatusIcon, taskStatuses, taskStatusTone } from '@/frontend/features/Fde/projectTaskPresentation'
import ProjectTaskCard from '@/frontend/features/Fde/ProjectTaskCard'
import ProjectTaskMenu from '@/frontend/features/Fde/ProjectTaskMenu'
import type { TaskSurfaceProps } from '@/frontend/features/Fde/ProjectTaskCanvas'

export default function ProjectTaskBoard(props: TaskSurfaceProps) {
  return (
    <div className="h-full overflow-auto px-4 pt-3 lg:px-5 @[68rem]:overflow-y-clip">
      <div className="flex min-h-full gap-4 @[68rem]:h-full @[68rem]:min-h-0">
        {taskStatuses.map((status) => {
          const group = props.tasks.filter((task) => task.status === status)
          return (
            <section
              key={status}
              aria-label={props.copy.management.statuses[status]}
              className="flex min-w-64 flex-1 basis-0 flex-col @[68rem]:h-full"
            >
              <header className="flex h-10 shrink-0 items-center gap-2 px-1">
                <span className={taskStatusTone[status].split(' ')[0]}>
                  <TaskStatusIcon status={status} className="size-3.5" />
                </span>
                <h3 className="text-xs font-medium">{props.copy.management.statuses[status]}</h3>
                <span className="text-xs tabular-nums text-muted-foreground">{group.length}</span>
              </header>
              <Droppable droppableId={status} isDropDisabled={!props.canManage || props.busy}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={cn(
                      'min-h-24 flex-1 space-y-2 rounded-lg px-0.5 pb-4 pt-1 transition-colors [scrollbar-width:thin] @[68rem]:overflow-y-auto',
                      snapshot.isDraggingOver && 'bg-primary/5 ring-1 ring-primary/15',
                    )}
                  >
                    {group.map((task, index) => (
                      <Draggable
                        key={task.id}
                        draggableId={task.id}
                        index={index}
                        isDragDisabled={!props.canManage || props.busy}
                        disableInteractiveElementBlocking
                      >
                        {(drag, state) => (
                          <ProjectTaskMenu
                            task={task}
                            canManage={props.canManage}
                            busy={props.busy}
                            onSelect={() => props.onSelect(task.id)}
                            onPatch={props.onPatch}
                            onCopy={props.onCopy}
                            copy={props.copy}
                          >
                            <div
                              ref={drag.innerRef}
                              {...drag.draggableProps}
                              style={drag.draggableProps.style as CSSProperties}
                              className={state.isDragging ? 'rounded-xl shadow-xl' : ''}
                            >
                              <ProjectTaskCard
                                task={task}
                                overdue={props.overdueIds.has(task.id)}
                                onSelect={() => props.onSelect(task.id)}
                                copy={props.copy}
                                dragHandleProps={drag.dragHandleProps}
                              />
                            </div>
                          </ProjectTaskMenu>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </section>
          )
        })}
      </div>
    </div>
  )
}
